import { createContext, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DEFAULT_REGION, PARAMS_FREE_PATHS, REGION_KEY } from 'shared/components/RegionSwitcher/constants';
import { setStoredRegion } from 'shared/components/RegionSwitcher/regionSessionStorage';
import { setCurrentRegion } from 'shared/components/RegionSwitcher/regionState';
import { isLoggedIn } from '@nordvpn/teams.core/lib/auth/utils/localStorage';
import { Features } from 'types/features/enums';
import { getPathWithRegion } from 'utils/routing/getPathWithRegion';
import { getRegionFromSearch } from 'utils/routing/getRegionFromSearch';
import { useFeatureFlag } from 'utils/useFeatureFlag';
import { jsx as _jsx } from "react/jsx-runtime";
var RegionContext = /*#__PURE__*/ createContext(DEFAULT_REGION);
export var RegionProvider = function RegionProvider(_ref) {
    var children = _ref.children;
    var location = useLocation();
    var history = useHistory();
    var retrievedRegion = getRegionFromSearch(location.search);
    var isMultiRegionEnabled = useFeatureFlag(Features.MultiRegionSwitcher);
    var region = isMultiRegionEnabled ? retrievedRegion : DEFAULT_REGION;
    /**
     * During render, not in an effect: React Query dispatches its requests from a child effect, which
     * runs before this component's effect would. The interceptor would then read the previous region
     * while the key already used the new one, caching one data centre's response under the other's
     * key. Axios currently defers that read by a microtask, which masks the ordering - incidentally,
     * not by contract: mark the request interceptors `synchronous` and it surfaces. Setting the
     * region here holds either way.
     *
     * Safe here: the value derives only from the url and the flag, and the assignment is idempotent.
     */
    setCurrentRegion(region);
    useEffect(function () {
        if (!isMultiRegionEnabled)
            return;
        setStoredRegion(region);
    }, [isMultiRegionEnabled, region]);
    useEffect(function () {
        if (!isMultiRegionEnabled)
            return;
        if (!isLoggedIn())
            return;
        if (PARAMS_FREE_PATHS.some(function (path) {
            return location.pathname.startsWith(path);
        }))
            return;
        var param = new URLSearchParams(location.search).get(REGION_KEY);
        if (param === region)
            return;
        history.replace(getPathWithRegion(location.pathname + location.search, region));
    }, [isMultiRegionEnabled, history, location.pathname, location.search, region]);
    return /*#__PURE__*/ _jsx(RegionContext.Provider, {
        value: region,
        children: children
    });
};
export var useRegion = function useRegion() {
    return useContext(RegionContext);
};
